/**
 * Message Handler
 *
 * Processes incoming postMessage requests from the main app.
 * Implements the signing protocol defined in messages.ts.
 */

import { supportsPRF, deriveKeyWithPRF, deriveKeyFromPRFOutput } from './prf-encryption';
import { deriveKeyWithPIN, generateSalt } from './pin-encryption';
import { generateP256Keypair, signWithP256, base64ToArray } from './p256-keygen';
import {
  storeSessionKeypair,
  loadSessionKeypair,
  hasStoredKeypair,
  getStoredKeypairInfo,
  deleteStoredKeypair,
  getUnlockRequirements,
  getStoredPublicKey,
} from './key-storage';
import { deriveRelayAddress, signWithRelay, deriveEd25519Keypair, publicKeyToStellarAddress, seedToStellarSecret } from './ed25519-relay';
import type { P256Keypair } from './p256-keygen';

// Allowed origins that can communicate with this iframe
const ALLOWED_ORIGINS = [
  'https://lumenbro.com',
  'https://www.lumenbro.com',
  'http://localhost:3000', // Development
  'http://localhost:3001', // Development alt
  'http://localhost:3002', // Development alt
  'http://localhost:3003', // Development alt
];

// Response types matching the protocol
type SignerResponse =
  | { type: 'INIT_OK'; hasPRF: boolean; hasKeypair: boolean; encryptionMethod?: 'prf' | 'pin' }
  | { type: 'HAS_KEYPAIR_RESULT'; hasKeypair: boolean }
  | { type: 'KEYPAIR_GENERATED'; publicKey: { x: string; y: string } }
  | { type: 'SIGNATURE_RESULT'; signature: string }
  | { type: 'PUBLIC_KEY'; publicKey: { x: string; y: string } | null }
  | { type: 'KEYPAIR_DELETED' }
  | { type: 'UNLOCKED' }
  | { type: 'LOCKED' }
  | { type: 'PIN_REQUIRED' }
  | { type: 'PRF_ADDRESS_RESULT'; address: string; publicKey: string }
  | { type: 'PRF_SIGNATURE_RESULT'; signature: string; publicKey: string; address: string }
  | { type: 'PRF_SECRET_RESULT'; secretKey: string; address: string }
  | { type: 'ERROR'; code: string; error: string };

// Cached decrypted keypair (cleared after use)
let cachedKeypair: P256Keypair | null = null;

/**
 * Initialize the message handler.
 * Call this on iframe load.
 */
export function initMessageHandler(): void {
  window.addEventListener('message', handleMessage);
  console.log('[SignerHandler] Message handler initialized');
}

/**
 * Handle incoming postMessage events.
 */
async function handleMessage(event: MessageEvent): Promise<void> {
  // Silently ignore messages from same origin (browser extension noise)
  if (event.origin === window.location.origin) {
    return;
  }

  // Validate origin
  if (!ALLOWED_ORIGINS.includes(event.origin)) {
    console.warn('[SignerHandler] Blocked message from unknown origin:', event.origin);
    return;
  }

  // Validate message format
  const data = event.data;
  if (!data || data.source !== 'lumenbro-session-signer' || !data.requestId || !data.payload) {
    return;
  }

  const { requestId, payload } = data;
  console.log('[SignerHandler] Received request:', payload.type);

  try {
    const response = await processRequest(payload);
    sendResponse(event.source as Window, event.origin, requestId, response);
  } catch (error: any) {
    console.error('[SignerHandler] Error processing request:', error);
    sendResponse(event.source as Window, event.origin, requestId, {
      type: 'ERROR',
      code: 'UNKNOWN_ERROR',
      error: error.message || 'Unknown error',
    });
  }
}

/**
 * Process a request and return the response.
 */
async function processRequest(payload: any): Promise<SignerResponse> {
  switch (payload.type) {
    case 'INIT':
      return handleInit();

    case 'HAS_KEYPAIR':
      return handleHasKeypair();

    case 'GENERATE_KEYPAIR':
      return handleGenerateKeypair(payload.usePin, payload.pin, payload.credentialId, payload.prfKey);

    case 'SESSION_SIGN':
      return handleSign(payload.payload, payload.pin);

    case 'GET_PUBLIC_KEY':
      return handleGetPublicKey();

    case 'DELETE_KEYPAIR':
      return handleDeleteKeypair();

    case 'UNLOCK':
      return handleUnlock(payload.pin);

    case 'UNLOCK_WITH_PRF':
      return handleUnlockWithPRF(payload.prfKey);

    case 'LOCK':
      return handleLock();

    // --- PRF Relay (stateless ed25519) ---
    case 'PRF_DERIVE_ADDRESS':
      return handlePRFDeriveAddress(payload.salt);

    case 'PRF_SIGN':
      return handlePRFSign(payload.payload, payload.salt);

    case 'PRF_EXPORT_SECRET':
      return handlePRFExportSecret(payload.salt);

    // --- PRF Relay with pre-computed output (iOS Safari: WebAuthn runs in main app) ---
    case 'PRF_DERIVE_ADDRESS_WITH_OUTPUT':
      return handlePRFDeriveAddressWithOutput(payload.prfOutputBase64);

    case 'PRF_SIGN_WITH_OUTPUT':
      return handlePRFSignWithOutput(payload.prfOutputBase64, payload.payload);

    case 'PRF_EXPORT_SECRET_WITH_OUTPUT':
      return handlePRFExportSecretWithOutput(payload.prfOutputBase64);

    default:
      return { type: 'ERROR', code: 'INVALID_MESSAGE', error: `Unknown request type: ${payload.type}` };
  }
}

/**
 * Handle INIT request.
 */
async function handleInit(): Promise<SignerResponse> {
  const hasPRF = await supportsPRF();
  const hasKeypair = await hasStoredKeypair();
  const info = hasKeypair ? await getStoredKeypairInfo() : null;

  return {
    type: 'INIT_OK',
    hasPRF,
    hasKeypair,
    encryptionMethod: info?.encryptionMethod,
  };
}

/**
 * Handle HAS_KEYPAIR request.
 */
async function handleHasKeypair(): Promise<SignerResponse> {
  return {
    type: 'HAS_KEYPAIR_RESULT',
    hasKeypair: await hasStoredKeypair(),
  };
}

/**
 * Handle GENERATE_KEYPAIR request.
 *
 * @param usePin - Force PIN mode even if PRF is available
 * @param pin - PIN for encryption (required if usePin=true or no PRF)
 * @param credentialIdBase64 - Credential ID for PRF mode (base64 encoded) - triggers WebAuthn in iframe
 * @param prfKeyBase64 - PRF key from main app login (base64 encoded) - no WebAuthn needed in iframe
 */
async function handleGenerateKeypair(
  usePin?: boolean,
  pin?: string,
  credentialIdBase64?: string,
  prfKeyBase64?: string
): Promise<SignerResponse> {
  // Check if keypair already exists
  if (await hasStoredKeypair()) {
    const publicKey = await getStoredPublicKey();
    if (publicKey) {
      console.log('[SignerHandler] Keypair already exists, returning existing public key');
      return { type: 'KEYPAIR_GENERATED', publicKey };
    }
  }

  // Determine encryption method - prefer prfKey from main app (no WebAuthn in iframe)
  const hasPRFKey = !usePin && !!prfKeyBase64;
  const hasPRFLocal = !usePin && !prfKeyBase64 && (await supportsPRF()) && !!credentialIdBase64;

  console.log('[SignerHandler] Generate keypair:', {
    usePin,
    hasPin: !!pin,
    hasCredentialId: !!credentialIdBase64,
    hasPRFKey,
    hasPRFLocal,
  });

  if (!hasPRFKey && !hasPRFLocal && !pin) {
    return { type: 'PIN_REQUIRED' };
  }

  // Generate new keypair
  const keypair = await generateP256Keypair();
  console.log('[SignerHandler] Generated new P-256 keypair');

  // Derive encryption key
  let encryptionKey: CryptoKey;
  let credentialId: string | undefined;
  let salt: Uint8Array | undefined;

  if (hasPRFKey && prfKeyBase64) {
    // PRF key from main app - NO WebAuthn needed in iframe!
    console.log('[SignerHandler] Using PRF key from main app (no WebAuthn in iframe)');
    try {
      const prfKeyBytes = base64ToArray(prfKeyBase64);
      encryptionKey = await deriveKeyFromPRFOutput(prfKeyBytes);
      // Store the credential ID for future unlocks if provided
      credentialId = credentialIdBase64;
      console.log('[SignerHandler] ✅ PRF key derived from main app successfully');
    } catch (prfError: any) {
      console.error('[SignerHandler] PRF key derivation failed:', prfError);
      return { type: 'PIN_REQUIRED' };
    }
  } else if (hasPRFLocal && credentialIdBase64) {
    // PRF mode via local WebAuthn - may fail on iOS Safari in iframe
    console.log('[SignerHandler] Using PRF mode with local WebAuthn (credential ID)');
    try {
      const credentialIdBytes = base64ToArray(credentialIdBase64);
      encryptionKey = await deriveKeyWithPRF(credentialIdBytes);
      credentialId = credentialIdBase64;
      console.log('[SignerHandler] ✅ PRF key derived via local WebAuthn');
    } catch (prfError: any) {
      console.error('[SignerHandler] Local PRF derivation failed:', prfError);
      // If PRF fails (user cancelled, not supported), fall back to PIN requirement
      return { type: 'PIN_REQUIRED' };
    }
  } else {
    // PIN mode
    if (!pin) {
      return { type: 'PIN_REQUIRED' };
    }
    console.log('[SignerHandler] Using PIN mode');
    salt = generateSalt();
    encryptionKey = await deriveKeyWithPIN(pin, salt);
  }

  // Store encrypted keypair
  await storeSessionKeypair(
    keypair,
    encryptionKey,
    (hasPRFKey || hasPRFLocal) ? 'prf' : 'pin',
    credentialId,
    salt
  );

  // Cache keypair for immediate signing
  cachedKeypair = keypair;

  console.log('[SignerHandler] ✅ Keypair stored with', (hasPRFKey || hasPRFLocal) ? 'PRF' : 'PIN', 'encryption');
  return { type: 'KEYPAIR_GENERATED', publicKey: keypair.publicKey };
}

/**
 * Handle SESSION_SIGN request.
 *
 * IMPORTANT: This does NOT auto-unlock. If the keypair isn't cached,
 * it returns LOCKED error. The main app must explicitly unlock first
 * (via UNLOCK_WITH_PRF from login or UNLOCK with PIN).
 *
 * This prevents unexpected WebAuthn prompts during transactions.
 */
async function handleSign(payloadBase64: string, pin?: string): Promise<SignerResponse> {
  // Check if keypair is cached (unlocked)
  if (!cachedKeypair) {
    // Not unlocked - return error instead of auto-unlocking
    // This prevents unexpected WebAuthn prompts
    const requirements = await getUnlockRequirements();
    if (!requirements) {
      return { type: 'ERROR', code: 'NO_KEYPAIR', error: 'No session keypair stored' };
    }

    // If PIN provided, try to unlock with it
    if (requirements.method === 'pin' && pin) {
      try {
        const encryptionKey = await deriveKeyWithPIN(pin, requirements.salt!);
        cachedKeypair = await loadSessionKeypair(encryptionKey);
      } catch (error: any) {
        return { type: 'ERROR', code: 'DECRYPTION_FAILED', error: error.message };
      }
    } else {
      // Session signer is locked - tell main app to fall back to passkey
      // or re-authenticate to get PRF key
      console.log('[SignerHandler] Session signer is locked, returning LOCKED error');
      return {
        type: 'ERROR',
        code: 'DECRYPTION_FAILED',
        error: 'Session signer is locked. Please re-authenticate to unlock.',
      };
    }
  }

  // Sign the payload
  try {
    const payload = base64ToArray(payloadBase64);
    console.log('[SignerHandler] Calling signWithP256, payload length:', payload.length);

    // Pass public key for JWK fallback if PKCS#8 import fails
    const signature = await signWithP256(
      payload,
      cachedKeypair.privateKey,
      cachedKeypair.publicKey
    );

    // Convert signature to base64
    let binary = '';
    for (let i = 0; i < signature.length; i++) {
      binary += String.fromCharCode(signature[i]);
    }
    const signatureBase64 = btoa(binary);

    console.log('[SignerHandler] ✅ Signing successful, signature length:', signature.length);

    // Clear cached keypair after signing (security best practice)
    // Note: In production, might want to keep cached for session duration
    // cachedKeypair = null;

    return { type: 'SIGNATURE_RESULT', signature: signatureBase64 };
  } catch (error: any) {
    console.error('[SignerHandler] ❌ Signing failed:', error.message, error);
    return { type: 'ERROR', code: 'SIGNING_FAILED', error: error.message };
  }
}

/**
 * Handle GET_PUBLIC_KEY request.
 */
async function handleGetPublicKey(): Promise<SignerResponse> {
  const publicKey = await getStoredPublicKey();
  return { type: 'PUBLIC_KEY', publicKey };
}

/**
 * Handle DELETE_KEYPAIR request.
 */
async function handleDeleteKeypair(): Promise<SignerResponse> {
  await deleteStoredKeypair();
  cachedKeypair = null;
  return { type: 'KEYPAIR_DELETED' };
}

/**
 * Handle UNLOCK request.
 */
async function handleUnlock(pin?: string): Promise<SignerResponse> {
  const requirements = await getUnlockRequirements();
  if (!requirements) {
    return { type: 'ERROR', code: 'NO_KEYPAIR', error: 'No session keypair stored' };
  }

  try {
    let encryptionKey: CryptoKey;

    if (requirements.method === 'prf' && requirements.credentialId) {
      encryptionKey = await deriveKeyWithPRF(base64ToArray(requirements.credentialId));
    } else if (requirements.method === 'pin' && requirements.salt) {
      if (!pin) {
        return { type: 'PIN_REQUIRED' };
      }
      encryptionKey = await deriveKeyWithPIN(pin, requirements.salt);
    } else {
      return { type: 'ERROR', code: 'INVALID_PIN', error: 'Invalid encryption method' };
    }

    cachedKeypair = await loadSessionKeypair(encryptionKey);
    return { type: 'UNLOCKED' };
  } catch (error: any) {
    return { type: 'ERROR', code: 'DECRYPTION_FAILED', error: error.message };
  }
}

/**
 * Handle UNLOCK_WITH_PRF request.
 *
 * This unlocks the session signer using a PRF key that was already
 * derived during the main app's login flow. This enables single-biometric
 * login+unlock without triggering a second WebAuthn ceremony.
 *
 * @param prfKeyBase64Url - PRF output from login, base64url-encoded
 */
async function handleUnlockWithPRF(prfKeyBase64Url?: string): Promise<SignerResponse> {
  if (!prfKeyBase64Url) {
    return { type: 'ERROR', code: 'INVALID_PIN', error: 'PRF key not provided' };
  }

  const requirements = await getUnlockRequirements();
  if (!requirements) {
    return { type: 'ERROR', code: 'NO_KEYPAIR', error: 'No session keypair stored' };
  }

  if (requirements.method !== 'prf') {
    return { type: 'ERROR', code: 'INVALID_PIN', error: 'Keypair uses PIN mode, not PRF' };
  }

  try {
    console.log('[SignerHandler] 🔓 Unlocking with PRF key from login...');

    // Decode base64url PRF key
    const prfKeyBytes = base64ToArray(prfKeyBase64Url);

    // Derive AES key from PRF output (no WebAuthn ceremony needed!)
    const encryptionKey = await deriveKeyFromPRFOutput(prfKeyBytes);

    // Decrypt and cache keypair
    cachedKeypair = await loadSessionKeypair(encryptionKey);

    console.log('[SignerHandler] ✅ Session signer unlocked via PRF from login!');
    return { type: 'UNLOCKED' };
  } catch (error: any) {
    console.error('[SignerHandler] PRF unlock failed:', error);
    return { type: 'ERROR', code: 'DECRYPTION_FAILED', error: error.message };
  }
}

/**
 * Handle LOCK request.
 *
 * Clears the cached keypair from memory, requiring re-authentication
 * before signing again. Does NOT delete the encrypted keypair from storage.
 */
function handleLock(): SignerResponse {
  cachedKeypair = null;
  console.log('[SignerHandler] 🔒 Session signer locked (cached keypair cleared)');
  return { type: 'LOCKED' };
}

// ============================================================================
// PRF Relay Handlers (Stateless ed25519)
// ============================================================================

// Default PRF salt for Stellar relay derivation
const DEFAULT_RELAY_SALT = 'lumenbro:stellar:relay:v1';

/**
 * Trigger WebAuthn PRF ceremony and return the raw PRF output.
 * This is the core biometric gate — no Face ID = no key material.
 */
async function getPRFOutput(salt: string): Promise<Uint8Array> {
  const saltBytes = new TextEncoder().encode(salt);

  // Determine rpId: must match the passkey's registration domain
  const isDev = window.location.hostname === 'localhost';
  const rpId = isDev ? 'localhost' : 'lumenbro.com';

  const publicKeyOptions: PublicKeyCredentialRequestOptions = {
    challenge: crypto.getRandomValues(new Uint8Array(32)),
    rpId,
    userVerification: 'required',
    extensions: {},
  };
  // PRF extension — not in all TypeScript definitions
  (publicKeyOptions.extensions as any).prf = {
    eval: { first: saltBytes },
  };

  const credential = (await navigator.credentials.get({
    publicKey: publicKeyOptions,
  })) as PublicKeyCredential | null;

  if (!credential) {
    throw new Error('WebAuthn ceremony cancelled');
  }

  const extensions = credential.getClientExtensionResults();
  // @ts-ignore PRF extension results
  const prfResults = extensions?.prf?.results;

  if (!prfResults?.first) {
    throw new Error('PRF extension not supported or failed');
  }

  return new Uint8Array(prfResults.first as ArrayBuffer);
}

/**
 * Helper to convert Uint8Array to base64.
 */
function arrayToBase64(arr: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < arr.length; i++) {
    binary += String.fromCharCode(arr[i]);
  }
  return btoa(binary);
}

/**
 * Handle PRF_DERIVE_ADDRESS — derive Stellar G-address from PRF.
 *
 * Triggers Face ID → PRF output → HKDF → ed25519 pubkey → G-address.
 * Nothing is stored. The keypair is never fully materialized (only pubkey).
 */
async function handlePRFDeriveAddress(salt?: string): Promise<SignerResponse> {
  try {
    const effectiveSalt = salt || DEFAULT_RELAY_SALT;
    console.log('[SignerHandler] PRF_DERIVE_ADDRESS: triggering WebAuthn PRF...');

    const prfOutput = await getPRFOutput(effectiveSalt);
    const { publicKey } = await deriveEd25519Keypair(prfOutput);
    const address = publicKeyToStellarAddress(publicKey);

    console.log('[SignerHandler] PRF_DERIVE_ADDRESS: derived', address.slice(0, 8) + '...');

    return {
      type: 'PRF_ADDRESS_RESULT',
      address,
      publicKey: arrayToBase64(publicKey),
    };
  } catch (error: any) {
    console.error('[SignerHandler] PRF_DERIVE_ADDRESS failed:', error);
    const code = error.name === 'NotAllowedError' ? 'WEBAUTHN_FAILED' : 'PRF_FAILED';
    return { type: 'ERROR', code, error: error.message };
  }
}

/**
 * Handle PRF_SIGN — sign a payload with PRF-derived ed25519 keypair.
 *
 * Triggers Face ID → PRF output → HKDF → ed25519 keypair → sign → discard.
 * The private key exists only during the signing operation.
 */
async function handlePRFSign(payloadBase64?: string, salt?: string): Promise<SignerResponse> {
  if (!payloadBase64) {
    return { type: 'ERROR', code: 'INVALID_MESSAGE', error: 'Missing payload to sign' };
  }

  try {
    const effectiveSalt = salt || DEFAULT_RELAY_SALT;
    const payload = base64ToArray(payloadBase64);

    console.log('[SignerHandler] PRF_SIGN: triggering WebAuthn PRF...');

    const prfOutput = await getPRFOutput(effectiveSalt);
    const result = await signWithRelay(prfOutput, payload);

    console.log('[SignerHandler] PRF_SIGN: signed, address:', result.address.slice(0, 8) + '...');

    return {
      type: 'PRF_SIGNATURE_RESULT',
      signature: arrayToBase64(result.signature),
      publicKey: arrayToBase64(result.publicKey),
      address: result.address,
    };
  } catch (error: any) {
    console.error('[SignerHandler] PRF_SIGN failed:', error);
    const code = error.name === 'NotAllowedError' ? 'WEBAUTHN_FAILED'
      : error.name === 'SecurityError' ? 'WEBAUTHN_FAILED'
      : 'SIGNING_FAILED';
    return { type: 'ERROR', code, error: error.message };
  }
}

/**
 * Handle PRF_EXPORT_SECRET — export the PRF-derived ed25519 secret key (S-address).
 *
 * Triggers Face ID → PRF output → HKDF → ed25519 seed → StrKey S-address.
 * The secret key is returned to the caller and immediately discarded here.
 */
async function handlePRFExportSecret(salt?: string): Promise<SignerResponse> {
  try {
    const effectiveSalt = salt || DEFAULT_RELAY_SALT;
    console.log('[SignerHandler] PRF_EXPORT_SECRET: triggering WebAuthn PRF...');

    const prfOutput = await getPRFOutput(effectiveSalt);
    const { publicKey, privateKey } = await deriveEd25519Keypair(prfOutput);
    const address = publicKeyToStellarAddress(publicKey);
    const secretKey = seedToStellarSecret(privateKey);

    console.log('[SignerHandler] PRF_EXPORT_SECRET: exported for', address.slice(0, 8) + '...');

    return {
      type: 'PRF_SECRET_RESULT',
      secretKey,
      address,
    };
  } catch (error: any) {
    console.error('[SignerHandler] PRF_EXPORT_SECRET failed:', error);
    const code = error.name === 'NotAllowedError' ? 'WEBAUTHN_FAILED' : 'PRF_FAILED';
    return { type: 'ERROR', code, error: error.message };
  }
}

// ============================================================================
// PRF handlers with pre-computed output (iOS Safari fallback)
// Main app runs WebAuthn, passes PRF output to iframe for key derivation.
// ============================================================================

async function handlePRFDeriveAddressWithOutput(prfOutputBase64?: string): Promise<SignerResponse> {
  if (!prfOutputBase64) {
    return { type: 'ERROR', code: 'INVALID_MESSAGE', error: 'Missing prfOutputBase64' };
  }
  try {
    const prfOutput = base64ToArray(prfOutputBase64);
    const { publicKey } = await deriveEd25519Keypair(prfOutput);
    const address = publicKeyToStellarAddress(publicKey);
    return { type: 'PRF_ADDRESS_RESULT', address, publicKey: arrayToBase64(publicKey) };
  } catch (error: any) {
    return { type: 'ERROR', code: 'PRF_FAILED', error: error.message };
  }
}

async function handlePRFSignWithOutput(prfOutputBase64?: string, payloadBase64?: string): Promise<SignerResponse> {
  if (!prfOutputBase64 || !payloadBase64) {
    return { type: 'ERROR', code: 'INVALID_MESSAGE', error: 'Missing prfOutputBase64 or payload' };
  }
  try {
    const prfOutput = base64ToArray(prfOutputBase64);
    const payload = base64ToArray(payloadBase64);
    const result = await signWithRelay(prfOutput, payload);
    return {
      type: 'PRF_SIGNATURE_RESULT',
      signature: arrayToBase64(result.signature),
      publicKey: arrayToBase64(result.publicKey),
      address: result.address,
    };
  } catch (error: any) {
    return { type: 'ERROR', code: 'SIGNING_FAILED', error: error.message };
  }
}

async function handlePRFExportSecretWithOutput(prfOutputBase64?: string): Promise<SignerResponse> {
  if (!prfOutputBase64) {
    return { type: 'ERROR', code: 'INVALID_MESSAGE', error: 'Missing prfOutputBase64' };
  }
  try {
    const prfOutput = base64ToArray(prfOutputBase64);
    const { publicKey, privateKey } = await deriveEd25519Keypair(prfOutput);
    const address = publicKeyToStellarAddress(publicKey);
    const secretKey = seedToStellarSecret(privateKey);
    return { type: 'PRF_SECRET_RESULT', secretKey, address };
  } catch (error: any) {
    return { type: 'ERROR', code: 'PRF_FAILED', error: error.message };
  }
}

/**
 * Send a response back to the main app.
 */
function sendResponse(
  target: Window,
  origin: string,
  requestId: string,
  payload: SignerResponse
): void {
  target.postMessage(
    {
      source: 'lumenbro-session-signer',
      version: 1,
      requestId,
      payload,
    },
    origin
  );
}
